import classNames from "classnames";
import { Entry } from "contentful";
import { ImgHTMLAttributes, SVGProps } from "react";
import React, { useCallback } from "react";

import { useTextDir } from "~/contexts/text-dir";
import Editorials from "~/types/editorials";

import iconPaths from "../../utils/icon-paths";
import styles from "./icon.module.scss";
import IconAsset from "./icon-asset";
import IconClose from "./icons/icon-close";

// list of icons that must be horizontally flipped on RTL languages
// follow this guide for understanding when to mirror icons
// https://m2.material.io/design/usability/bidirectionality.html#mirroring-elements
const mirroringIconsOnRtl = {
  kebabCase: [
    "arrow-left",
    "arrow-right",
    "chevron-left",
    "chevron-right",
    "star-25-filled",
    "star-half",
    "star-75-filled",
  ],
  pascalCase: [
    "IconArrowLeft",
    "IconArrowRight",
    "IconChevronLeft",
    "IconChevronRight",
    "IconStar25Filled",
    "IconStarHalf",
    "IconStar75Filled",
  ],
};

type Props = {
  icon?: React.ComponentType<{ className?: string; color?: string }>;
  name?: string | number | symbol;
  svgMedia?: Entry<Editorials.Asset>;
  width?: string | number;
  height?: string | number;
  value?: number | string;
} & Omit<SVGProps<SVGSVGElement>, "name"> &
  Omit<ImgHTMLAttributes<HTMLImageElement>, "placeholder">;

// The difference between Icon and Illustration components is that the icon component is wrapped within a <div />
// So the icon can be rendered along with a counter to display some data
// The Illustraion component instead is agnostic and does not have any container <div />
export default function Icon(props: Props) {
  const { icon, value, svgMedia, name, children, ...rest } = props;
  const IconComponent = icon;
  const dir = useTextDir();

  const isIconRTL =
    dir === "rtl" &&
    (mirroringIconsOnRtl.kebabCase.includes(String(name)) ||
      mirroringIconsOnRtl.pascalCase.includes(IconComponent?.name || ""));

  const iconWrapper = useCallback(
    (iconContent: React.ReactNode) => {
      return (
        <span className={classNames(styles.iconContainer, rest.className)}>
          {/* SVG element */}
          {iconContent}
          {/* Possible icon counter */}
          {typeof value === "number" || (typeof value === "string" && value.length > 0) ? (
            <span className={styles.iconValue}>{value}</span>
          ) : null}
        </span>
      );
    },
    [rest.className, value]
  );

  if (svgMedia) {
    return <IconAsset svgMedia={svgMedia} value={value} isIconRTL={isIconRTL} {...rest} />;
  } else if (name && name in iconPaths) {
    const DynamicIcon = iconPaths[String(name)];
    return iconWrapper(<DynamicIcon {...rest} className={classNames(isIconRTL ? styles.iconRtl : null)} />);
  } else if (IconComponent) {
    return iconWrapper(<IconComponent {...rest} className={classNames(isIconRTL ? styles.iconRtl : null)} />);
  } else if (process.env.NODE_ENV === "development") {
    return <IconClose {...rest} color="red" />;
  } else {
    return null;
  }
}
